"use client";
import "@/styles/App.css";
import "@/styles/Contact.css";
import "@/styles/Plugins.css";
import "@/styles/MiniCalendar.css";
import "@/styles/dashboard-classic.css";

import { ChakraProvider } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { SessionProvider, useSession } from "next-auth/react";
import React, { type ReactNode, useEffect, useMemo, useState } from "react";
import { PullToRefresh, ScrollToTop } from "@/components/ui";
import { PageTypeAdmin, PageTypeGlobal, PageTypePortal, QueryKeys } from "@/lib/types";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { redirectToLoginAfterAuthFailure } from "@/lib/utils/auth/redirectToLogin";
import { hydratePublicLayoutQueries } from "@/lib/utils/prefetch/seedPublicQueryData";

import { getBrandColors } from "@/lib/theme/chakra/getBrandColors";
import initialTheme from "@/lib/theme/chakra/theme";
import { DashboardThemeProvider } from "@/components/dashboard/DashboardThemeProvider";
import { DashboardAppearanceBodySync } from "@/components/dashboard/DashboardAppearanceBodySync";
import { DashboardChakraColorModeSync } from "@/components/dashboard/DashboardChakraColorModeSync";
import { FontSizeProvider } from "@/components/dashboard/FontSizeProvider";
import { GlobalGrainOverlay } from "@/lib/theme/tokens/GlobalGrainOverlay";
import { ThemeColorMeta } from "@/lib/theme/tokens/ThemeColorMeta";

const _NoSSR = ({ children }: any) => (
  <React.Fragment>{children}</React.Fragment>
);

const NoSSR = dynamic(() => Promise.resolve(_NoSSR), {
  ssr: false,
});

type UserRoles = {
  isAdmin: boolean;
  isTeacher: boolean;
  isStaff: boolean;
  isMember: boolean;
};

function ThemeWrapper({
  children,
  initialUserRoles,
  initialPersonalization,
  initialPublicLayout,
}: {
  children: ReactNode;
  initialUserRoles: UserRoles;
  initialPersonalization?: {
    colorPalette: import("@/lib/theme/color-palettes").ColorPaletteId;
    customColor: string | null;
    grainEnabled: boolean;
    dashboardAppearance?: import("@/lib/theme/dashboard-appearance").DashboardAppearance;
  } | null;
  initialPublicLayout?: import("@/lib/utils/prefetch/seedPublicQueryData").PublicLayoutSeed | null;
}) {
  const { data: session } = useSession();
  const pathname = usePathname();
  const [mini, setMini] = useState(false);
  const [contrast, setContrast] = useState(false);
  const [hovered, setHovered] = useState(false);

  const queryClient = useMemo(() => {
    const isPublicRoute = (pathname: string): boolean => {
      // Public global routes
      if (
        pathname === PageTypeGlobal.Home ||
        pathname.startsWith(PageTypeGlobal.Classes) ||
        pathname.startsWith(PageTypeGlobal.Teachers) ||
        pathname.startsWith(PageTypeGlobal.Locations) ||
        pathname.startsWith(PageTypeGlobal.Contact) ||
        pathname.startsWith(PageTypeGlobal.Terms) ||
        pathname.startsWith("/contractor-packet")
      ) {
        return true;
      }

      // Auth routes (public)
      if (
        pathname.startsWith("/portal/auth/") ||
        pathname.startsWith("/admin/auth/")
      ) {
        return true;
      }

      // Error pages
      if (
        pathname === "/forbidden" ||
        pathname === "/403" ||
        pathname === "/404"
      ) {
        return true;
      }

      return false;
    };

    const handle401Error = () => {
      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname;

        // Don't redirect if it's a public route
        if (isPublicRoute(currentPath)) {
          return;
        }

        void redirectToLoginAfterAuthFailure(currentPath);
      }
    };

    const client = new QueryClient({
      defaultOptions: {
        queries: {
          retry: (failureCount, error: any) => {
            const status = error?.response?.status ?? error?.status;
            if (status === 401) {
              handle401Error();
              return false;
            }
            
            if (status != null && status >= 400 && status < 500) {
              return false;
            }

            return failureCount < 3;
          },
        },
        mutations: {
          retry: (failureCount, error: any) => {
            const status = error?.response?.status ?? error?.status;
            if (status === 401) {
              handle401Error();
              return false;
            }
            if (status != null && status >= 400 && status < 500) {
              return false;
            }
            return failureCount < 3;
          },
        },
      },
    });

    if (initialPersonalization) {
      client.setQueryData(
        [QueryKeys.TenantPersonalization, resolveEffectiveTenantNumber()],
        initialPersonalization,
      );
    }

    if (initialPublicLayout) {
      hydratePublicLayoutQueries(client, initialPublicLayout);
    }

    client.getQueryCache().subscribe((event) => {
      if (event?.type === "updated" && event?.query?.state?.error) {
        const error = event.query.state.error as any;
        if (error?.status === 401 || error?.response?.status === 401) {
          handle401Error();
        }
      }
    });

    client.getMutationCache().subscribe((event) => {
      if (event?.type === "updated" && event?.mutation?.state?.error) {
        const error = event.mutation.state.error as any;
        if (error?.status === 401 || error?.response?.status === 401) {
          handle401Error();
        }
      }
    });

    return client;
  }, []);

  const getRolesForTheme = (currentPathname: string | null): UserRoles => {
    const isOnAuthPage = currentPathname?.includes("/auth/") ?? false;

    if (isOnAuthPage) {
      return {
        isAdmin: false,
        isTeacher: false,
        isStaff: false,
        isMember: false,
      };
    }

    return {
      isAdmin: session?.user?.isAdmin ?? initialUserRoles.isAdmin,
      isTeacher: session?.user?.isTeacher ?? initialUserRoles.isTeacher,
      isStaff: session?.user?.isStaff ?? initialUserRoles.isStaff,
      isMember: session?.user?.isMember ?? initialUserRoles.isMember,
    };
  };

  const [theme, setTheme] = useState<{ [x: string]: any }>(() => {
    const initialRoles = getRolesForTheme(pathname);
    const brandColors = getBrandColors(initialRoles);
    return {
      ...initialTheme,
      colors: {
        ...initialTheme.colors,
        ...brandColors,
      },
    };
  });

  useEffect(() => {
    const rolesToUse = getRolesForTheme(pathname);
    const brandColors = getBrandColors(rolesToUse);
    setTheme((prevTheme) => ({
      ...prevTheme,
      colors: {
        ...prevTheme.colors,
        ...brandColors,
      },
    }));
  }, [pathname]);

  const configuratorValue = useMemo(
    () => ({
      mini,
      setMini,
      theme,
      setTheme,
      hovered,
      setHovered,
      contrast,
      setContrast,
    }),
    [mini, theme, hovered, contrast],
  );

  return (
    <NoSSR>
        <QueryClientProvider client={queryClient}>
          <ChakraProvider
            theme={theme}
            toastOptions={{
              defaultOptions: {
                isClosable: true,
              },
            }}
          >
            <DashboardThemeProvider initialPersonalization={initialPersonalization}>
              <DashboardAppearanceBodySync />
              <DashboardChakraColorModeSync />
              <ThemeColorMeta />
              <FontSizeProvider />
              <GlobalGrainOverlay />
              {children}
              <ScrollToTop />
              <PullToRefresh />
            </DashboardThemeProvider>
          </ChakraProvider>
        </QueryClientProvider>
    </NoSSR>
  );
}

export default function AppWrappers({
  children,
  initialUserRoles,
  initialPersonalization,
  initialPublicLayout,
}: {
  children: ReactNode;
  initialUserRoles: UserRoles;
  initialPersonalization?: {
    colorPalette: import("@/lib/theme/color-palettes").ColorPaletteId;
    customColor: string | null;
    grainEnabled: boolean;
    dashboardAppearance?: import("@/lib/theme/dashboard-appearance").DashboardAppearance;
  } | null;
  initialPublicLayout?: import("@/lib/utils/prefetch/seedPublicQueryData").PublicLayoutSeed | null;
}) {
  return (
    <SessionProvider
      refetchInterval={25 * 60}
      refetchOnWindowFocus={false}
    >
      <ThemeWrapper 
        initialUserRoles={initialUserRoles}
        initialPersonalization={initialPersonalization}
        initialPublicLayout={initialPublicLayout}
      >
        {children}
      </ThemeWrapper>
    </SessionProvider>
  );
}
